import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useCompetitions } from '@/hooks/useCompetitions';
import { useTeams } from '@/hooks/useTeams';
import { useUser } from '@/hooks/useUser';

// Components
import AppHeader from '@/components/layout/AppHeader';
import AppFooter from '@/components/layout/AppFooter';
import NavigationTabs from '@/components/layout/NavigationTabs';
import GameCard from '@/components/game/GameCard';
import GameCardSkeleton from '@/components/game/GameCardSkeleton';
import LeaderboardSection from '@/components/leaderboard/LeaderboardSection';
import { CompetitionView } from '@/components/screens/CompetitionView';
import LeagueLeaderboardView from '@/components/leaderboard/LeagueLeaderboardView';
import LeagueCard from '@/components/leaderboard/LeagueCard';
import EmptyState from '@/components/shared/EmptyState';
import WelcomeScreen from '@/components/screens/WelcomeScreen';
import HowToPlayScreen from '@/components/screens/HowToPlayScreen';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import {
  LeagueCreatedModal,
  PicksSubmittedModal,
  ClearSelectionsModal,
  CreateLeagueModal,
  JoinLeagueModal,
} from '@/components/modals/Bet99Modal';
import Picks from '@/components/screens/Picks';
import { useLeagues } from '@/hooks/useLeagues';
import { League } from '../types/user';

const ENABLE_MOBILE_CONTAINER = false;

const Bet99PickEm: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const [_selectedTeams, setSelectedTeams] = useState<Record<string, string>>({});
  const [_selectedProps, setSelectedProps] = useState<Record<string, 'yes' | 'no'>>({});

  // Modal states
  const [showLeagueCreated, setShowLeagueCreated] = useState(false);
  const [showCreateLeague, setShowCreateLeague] = useState(false);
  const [showJoinLeague, setShowJoinLeague] = useState(false);
  const [createdLeaguePin, setCreatedLeaguePin] = useState('');
  const [showPicksSubmitted, setShowPicksSubmitted] = useState(false);
  const [showClearSelections, setShowClearSelections] = useState(false);

  const {
    leagues: friendsLeagues,
    createLeague,
    joinLeague,
    isCreating,
    isJoining,
    isLoading: isLoadingLeagues,
  } = useLeagues();

  // Get data from hooks
  const { competitions, isLoading } = useCompetitions();
  const { getTeamPair: _getTeamPair } = useTeams();
  const { currentUser: _currentUser } = useUser();

  // Check if user has seen welcome screen
  const hasSeenWelcome = localStorage.getItem('hasSeenWelcome') === 'true';

  // Determine current screen from URL
  const currentScreen = useMemo(() => {
    const path = location.pathname;

    if (path.startsWith('/picks')) return 'picks';
    if (path === '/welcome') return 'welcome';
    if (path === '/how-to-play') return 'howToPlay';
    if (path.startsWith('/competition/')) return 'competition';
    if (path.startsWith('/league/')) return 'leagueLeaderboard';
    if (path.startsWith('/main') || path === '/') {
      // Show welcome screen on first load if user hasn't seen it
      if (!hasSeenWelcome && path === '/') {
        return 'welcome';
      }
      return 'main';
    }
    return hasSeenWelcome ? 'main' : 'welcome';
  }, [location.pathname, hasSeenWelcome]);

  // Get active tab from URL params or default to 'play'
  const activeTab = params.tab || searchParams.get('tab') || 'play';

  // Get game ID and league info from URL params
  const currentGameId = params.gameId || '';
  const currentLeague = useMemo(() => {
    const leagueId = decodeURIComponent(params.leagueId || '');
    const color = (searchParams.get('color') as 'orange' | 'blue') || 'orange';
    return { id: leagueId, color };
  }, [params.leagueId, searchParams]);

  const _handleTeamSelect = (gameId: string, teamId: string) => {
    setSelectedTeams(prev => ({ ...prev, [gameId]: teamId }));
  };

  const _handlePropSelect = (gameId: string, answer: 'yes' | 'no') => {
    setSelectedProps(prev => ({ ...prev, [gameId]: answer }));
  };

  const _handleSubmitPicks = () => {
    setShowPicksSubmitted(true);
  };

  const handleOpenCreateLeague = () => {
    setShowCreateLeague(true);
  };

  const handleSubmitCreateLeague = async (title: string) => {
    const response = await createLeague(title);
    const code = response?.league?.code ?? '';
    if (code) {
      setCreatedLeaguePin(code);
      setShowCreateLeague(false);
      setShowLeagueCreated(true);
    }
  };

  const handleOpenJoinLeague = () => {
    setShowJoinLeague(true);
  };

  const handleSubmitJoinLeague = async (code: string) => {
    await joinLeague(code);
    setShowJoinLeague(false);
  };

  const handleClearSelections = () => {
    setSelectedTeams({});
    setSelectedProps({});
    setShowClearSelections(false);
  };

  const handleGameAction = (gameId: string) => {
    navigate(`/competition/${gameId}`);
  };

  const handleLeagueLeaderboard = (league: League, show?: 'settings' | 'code') => {
    navigate(`/league/${encodeURIComponent(league.id)}?color=${league.color}&show=${show}`);
  };

  const handleTabChange = (tab: string) => {
    navigate(`/main/${tab}`);
  };

  if (currentScreen === 'leagueLeaderboard') {
    return (
      <div
        className={
          ENABLE_MOBILE_CONTAINER
            ? 'min-h-screen bg-gray-100 flex items-center justify-center p-4'
            : 'h-screen'
        }
      >
        {/* Mobile Container */}
        <div
          className={
            ENABLE_MOBILE_CONTAINER
              ? 'w-full max-w-sm h-screen bg-app-background rounded-2xl shadow-2xl overflow-hidden relative'
              : 'h-full'
          }
        >
          <LeagueLeaderboardView
            leagueId={currentLeague.id}
            leagueColor={currentLeague.color}
            onBack={() => navigate('/main/friends')}
          />
        </div>
      </div>
    );
  }

  if (currentScreen === 'competition') {
    return (
      <div
        className={
          ENABLE_MOBILE_CONTAINER
            ? 'min-h-screen bg-gray-100 flex items-center justify-center p-4'
            : ''
        }
      >
        {/* Mobile Container */}
        <div
          className={
            ENABLE_MOBILE_CONTAINER
              ? 'w-full max-w-sm h-screen max-h-[800px] bg-app-background rounded-2xl shadow-2xl overflow-hidden relative'
              : ''
          }
        >
          <CompetitionView
            gameId={currentGameId}
            leagueId={searchParams.get('leagueId') || undefined}
            onBack={() => navigate('/main/play')}
          />
        </div>
      </div>
    );
  }

  // Screen routing
  if (currentScreen === 'welcome') {
    return (
      <div
        className={
          ENABLE_MOBILE_CONTAINER
            ? 'min-h-screen bg-gray-100 flex items-center justify-center p-4'
            : ''
        }
      >
        {/* Mobile Container */}
        <div
          className={
            ENABLE_MOBILE_CONTAINER
              ? 'w-full max-w-sm h-screen max-h-[800px] bg-app-background rounded-2xl shadow-2xl overflow-hidden relative'
              : ''
          }
        >
          <WelcomeScreen
            onHowToPlay={() => navigate('/how-to-play')}
            onLetsPlay={() => {
              localStorage.setItem('hasSeenWelcome', 'true');
              navigate('/main/play');
            }}
          />
        </div>
      </div>
    );
  }

  if (currentScreen === 'howToPlay') {
    return (
      <div
        className={
          ENABLE_MOBILE_CONTAINER
            ? 'min-h-screen bg-gray-100 flex items-center justify-center p-4'
            : ''
        }
      >
        {/* Mobile Container */}
        <div
          className={
            ENABLE_MOBILE_CONTAINER
              ? 'w-full max-w-sm h-screen max-h-[800px] bg-app-background rounded-2xl shadow-2xl overflow-hidden relative'
              : ''
          }
        >
          <HowToPlayScreen
            onBack={() => navigate('/welcome')}
            onGotIt={() => {
              localStorage.setItem('hasSeenWelcome', 'true');
              navigate('/main/play');
            }}
          />
        </div>
      </div>
    );
  }

  // Main app content
  const renderContent = () => {
    switch (activeTab) {
      case 'play': {
        if (isLoading) {
          return (
            <div className="space-y-6">
              {[...Array(5)].map((_, index) => (
                <motion.div
                  key={`skeleton-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  <GameCardSkeleton />
                </motion.div>
              ))}
            </div>
          );
        }
        const activeGames = competitions
          .filter(g => g.state !== 'final')
          .sort((a, b) => {
            // Sort by isOpen: open games first (true before false)
            if (a.isOpen !== b.isOpen) {
              return a.isOpen ? -1 : 1;
            }
            // Then sort by date (soonest first)
            return a.gameDate.getTime() - b.gameDate.getTime();
          });
        return activeGames.length > 0 ? (
          <div className="space-y-6">
            {/* Games count text */}
            <p
              className="text-white text-center font-gilroy font-bold"
              style={{
                fontSize: '16px',
                lineHeight: '20px',
              }}
            >
              {t('pages.gamesCount', {
                count: activeGames.length,
                plural: activeGames.length !== 1 ? 's' : '',
              })}
            </p>

            <AnimatePresence mode="popLayout">
              {activeGames.map((game, index) => (
                <motion.div
                  key={game.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  <GameCard
                    id={game.id}
                    title={game.title}
                    prize={game.prize}
                    state={game.state}
                    gameDate={game.gameDate}
                    hasSelections={game.hasSelections}
                    isLocked={!game.isOpen}
                    currentPoints={game.currentPoints}
                    totalPicks={game.questions.length}
                    onEdit={() => handleGameAction(game.id)}
                    onPlay={() => handleGameAction(game.id)}
                    onView={() => handleGameAction(game.id)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <EmptyState title={t('pages.noGamesAvailable')} showAdSpace={false} />
        );
      }

      case 'recent': {
        if (isLoading) {
          return (
            <div className="space-y-6">
              {[...Array(3)].map((_, index) => (
                <motion.div
                  key={`skeleton-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  <GameCardSkeleton />
                </motion.div>
              ))}
            </div>
          );
        }
        const recentGames = competitions.filter(g => g.state === 'final');
        return recentGames.length > 0 ? (
          <div className="space-y-6">
            {/* Recent results count text */}
            <p
              className="text-white text-center font-gilroy font-bold"
              style={{
                fontSize: '16px',
                lineHeight: '20px',
              }}
            >
              {t('pages.recentResults', { count: recentGames.length })}
            </p>

            <AnimatePresence mode="popLayout">
              {recentGames.map((game, index) => (
                <motion.div
                  key={game.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  <GameCard
                    id={game.id}
                    title={game.title}
                    prize={game.prize}
                    state={game.state}
                    gameDate={game.gameDate}
                    finalScore={game.finalScore}
                    hasSelections={game.hasSelections}
                    totalPicks={game.questions.length}
                    correctPicks={game.correctPicks}
                    onView={() => handleGameAction(game.id)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <EmptyState title={t('pages.noCompletedGames')} showAdSpace={false} />
        );
      }

      case 'leaderboard':
        if (isLoading) {
          return <LoadingSpinner message="Loading leaderboard..." />;
        }
        return (
          <div className="space-y-6">
            <LeaderboardSection title="Global Leaderboard" />
          </div>
        );

      case 'friends':
        return (
          <div className="space-y-6" data-test-id="friends-tab-content">
            {/* League Cards */}
            <div className="space-y-4" data-test-id="friends-league-list">
              {isLoadingLeagues && <LoadingSpinner message="Loading your leagues..." />}
              {!isLoadingLeagues &&
                friendsLeagues.map(league => (
                  <LeagueCard
                    key={league.id}
                    league={league}
                    onViewLeaderboard={() => handleLeagueLeaderboard(league)}
                    onAddUser={() => handleLeagueLeaderboard(league, 'code')}
                    onSettings={() => handleLeagueLeaderboard(league, 'settings')}
                  />
                ))}
              {!isLoadingLeagues && friendsLeagues.length === 0 && (
                <div
                  className="bg-game-card rounded-xl p-6 text-center"
                  data-test-id="friends-empty-state"
                >
                  <p className="text-text-secondary">
                    You're not in any friends leagues yet. Create one or join with a PIN below.
                  </p>
                </div>
              )}
            </div>

            {/* Challenge Friends Section */}
            <div className="bg-game-card rounded-xl p-6 text-center">
              <div className="flex justify-center items-center gap-4 mb-4">
                <span className="text-2xl">😄</span>
                <h3 className="text-text-primary font-bold text-xl">CHALLENGE YOUR FRIENDS</h3>
                <span className="text-2xl">😎</span>
              </div>

              <p className="text-text-secondary mb-6">
                Create a private leaderboard for you and your friends!
              </p>

              <div className="space-y-3">
                <div className="flex gap-3">
                  <Button
                    data-test-id="open-join-league-btn"
                    variant="secondary"
                    onClick={handleOpenJoinLeague}
                    className="flex-1"
                  >
                    Join a League
                  </Button>
                  <Button
                    data-test-id="open-create-league-btn"
                    variant="bet99"
                    onClick={handleOpenCreateLeague}
                    className="flex-1"
                  >
                    Create New League
                  </Button>
                </div>

                <p className="text-text-muted text-sm">
                  Received an invite? Join using your PIN you received
                </p>
              </div>
            </div>
          </div>
        );

      case 'picks':
        return (
          <div>
            <Picks />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className={
        ENABLE_MOBILE_CONTAINER
          ? 'min-h-screen bg-gray-100 flex items-center justify-center p-4'
          : ''
      }
    >
      {/* Mobile Container */}
      <div
        className={
          ENABLE_MOBILE_CONTAINER
            ? 'w-full max-w-sm h-screen max-h-[800px] bg-app-background rounded-2xl shadow-2xl overflow-hidden relative'
            : ''
        }
      >
        <div
          className={
            ENABLE_MOBILE_CONTAINER ? 'h-full overflow-y-auto' : 'min-h-screen flex flex-col'
          }
        >
          <AppHeader />

          <main className="px-4 pb-6 flex-1">
            <NavigationTabs activeTab={activeTab} onTabChange={handleTabChange} />

            <div key={activeTab}>{renderContent()}</div>
          </main>

          <AppFooter />
        </div>
      </div>

      {/* Other Modals */}
      <CreateLeagueModal
        isOpen={showCreateLeague}
        onClose={() => setShowCreateLeague(false)}
        onSubmit={handleSubmitCreateLeague}
        isSubmitting={isCreating}
      />

      <JoinLeagueModal
        isOpen={showJoinLeague}
        onClose={() => setShowJoinLeague(false)}
        onSubmit={handleSubmitJoinLeague}
        isSubmitting={isJoining}
      />

      <LeagueCreatedModal
        isOpen={showLeagueCreated}
        onClose={() => setShowLeagueCreated(false)}
        pin={createdLeaguePin}
        onViewLeague={() => {
          setShowLeagueCreated(false);
          navigate('/main/friends');
        }}
      />

      <PicksSubmittedModal
        isOpen={showPicksSubmitted}
        onClose={() => setShowPicksSubmitted(false)}
        potentialWinnings="$813.95"
        onAddToBetSlip={() => setShowPicksSubmitted(false)}
        onReturnToLobby={() => setShowPicksSubmitted(false)}
      />

      <ClearSelectionsModal
        isOpen={showClearSelections}
        onClose={() => setShowClearSelections(false)}
        onClearPicks={handleClearSelections}
      />
    </div>
  );
};

export default Bet99PickEm;
